import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Card, Typography } from '@material-ui/core';

/**
 * ErrorBoundary component to catch React rendering errors and prevent full page crashes
 * This prevents the "grey screen" issue by gracefully handling errors
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Optionally reload the page
    if (this.props.reloadOnReset) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: 'background.default',
            p: 3
          }}
        >
          <Card
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: 'center'
            }}
            variant="outlined"
          >
            <Typography
              variant="h4"
              color="error"
              sx={{ mb: 2 }}
            >
              Something went wrong
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ mb: 3 }}
            >
              The page encountered an error and couldn't render properly.
              {this.props.showDetails && this.state.error && (
                <>
                  <br />
                  <br />
                  <strong>Error:</strong> {this.state.error.toString()}
                </>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </Box>
            {this.props.showDetails && this.state.errorInfo && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: 'neutral.100',
                  borderRadius: 1,
                  textAlign: 'left',
                  maxHeight: 200,
                  overflow: 'auto'
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
                >
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Box>
            )}
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  reloadOnReset: PropTypes.bool,
  showDetails: PropTypes.bool
};



export default ErrorBoundary;
